import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "@/api/client";
import type { components } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { CONFIDENCE, INTEL_KINDS, PACK_TITLE, WATCH_TITLE, dateWords, errorMessage, orNull } from "@/features/think-tank/ttModel";

type TtIntelView = components["schemas"]["TtIntelView"];
type TtEntry = components["schemas"]["TtIntelEntry"];
type TtSource = components["schemas"]["TtSourcePublic"];
type TtPackItem = components["schemas"]["TtPackItem"];
type Kind = TtEntry["kind"];
type Confidence = TtEntry["confidence"];

const KEY = ["admin", "think-tank"] as const;

/** Intel: the market from the outside, and what our own people say about other products. */
export function Intel() {
  const qc = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const query = useQuery({
    queryKey: [...KEY, "intel"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/think-tank/intel");
      if (error) throw new Error("failed to load");
      return data!;
    },
  });
  const refresh = () => qc.invalidateQueries({ queryKey: [...KEY] });
  const v = query.data;
  if (query.isLoading) return <p className="text-sm text-zinc-500">Loading…</p>;
  if (query.isError || !v) {
    return (
      <Card><CardBody>
        <p role="alert" className="text-sm text-red-700">Couldn’t load Intel. Your session may have expired.</p>
        <Button size="sm" variant="secondary" className="mt-3" onClick={() => void query.refetch()}>Retry</Button>
      </CardBody></Card>
    );
  }
  return (
    <div className="space-y-4">
      <Watch v={v} open={open} onOpen={setOpen} onChanged={refresh} />
      {open ? <Timeline key={open} id={open} sources={v.sources} onClose={() => setOpen(null)} onChanged={refresh} /> : null}
      <Pack v={v} onChanged={refresh} />
    </div>
  );
}

function Watch({ v, open, onOpen, onChanged }: { v: TtIntelView; open: string | null; onOpen: (id: string) => void; onChanged: () => void }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [what, setWhat] = useState("");
  const [url, setUrl] = useState("");
  const add = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST("/api/v1/admin/think-tank/competitors", { body: { name: name.trim(), what: orNull(what), url: orNull(url) } });
      if (error) throw new Error(errorMessage(error, "Couldn’t add it."));
      return data!;
    },
    onSuccess: d => { setAdding(false); setName(""); setWhat(""); setUrl(""); onChanged(); onOpen(d.id); },
  });
  return (
    <Card data-testid="tt-watch"><CardBody>
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold">{WATCH_TITLE}</h2>
        <Button size="sm" variant="secondary" data-testid="tt-watch-add" onClick={() => setAdding(!adding)}>+ Add</Button>
      </div>
      {adding ? (
        <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]" role="group" aria-label="Watch a competitor">
          <input aria-label="Name" placeholder="Company name" value={name} onChange={e => setName(e.target.value)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm" data-testid="tt-watch-name" />
          <input aria-label="What they do" placeholder="What they do" value={what} onChange={e => setWhat(e.target.value)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm" />
          <input aria-label="Website" placeholder="Website (https://…)" value={url} onChange={e => setUrl(e.target.value)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm" />
          <Button size="sm" disabled={!name.trim() || add.isPending} data-testid="tt-watch-save" onClick={() => add.mutate()}>Watch</Button>
          {add.isError ? <p role="alert" className="text-sm text-red-700 sm:col-span-4">{add.error.message}</p> : null}
        </div>
      ) : null}
      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {v.competitors.map(c => (
          <button key={c.id} type="button" data-testid={`tt-competitor-${c.id}`} onClick={() => onOpen(c.id)}
            className={`rounded-md border p-3 text-left hover:bg-zinc-50 ${open === c.id ? "border-zinc-900" : "border-zinc-200"}`}>
            <span className="block font-medium">{c.name}{c.sample ? <span className="ml-2 rounded bg-zinc-100 px-1.5 py-0.5 text-xs font-normal text-zinc-500">Sample</span> : null}</span>
            <span className="block text-xs text-zinc-500">{c.what}</span>
            <span className={`mt-1 block text-xs ${c.changes_30d ? "text-amber-800" : "text-zinc-400"}`}>{c.line}</span>
          </button>
        ))}
      </div>
      {!v.competitors.length ? <p className="py-2 text-sm text-zinc-500">Nobody yet. Add the first company to watch.</p> : null}
      <p className="mt-2 text-xs text-zinc-500">Each card opens a timeline: launches, prices, reviews, hiring and funding, with links. AI watching runs when keys and web research are connected; until then, entries are added by hand.</p>
    </CardBody></Card>
  );
}

function Timeline({ id, sources, onClose, onChanged }: { id: string; sources: TtSource[]; onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [editingCompany, setEditingCompany] = useState(false);
  const query = useQuery({
    queryKey: [...KEY, "competitor", id],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/think-tank/competitors/{competitor_id}", { params: { path: { competitor_id: id } } });
      if (error) throw new Error("failed to load");
      return data!;
    },
  });
  const changed = () => { void qc.invalidateQueries({ queryKey: [...KEY, "competitor", id] }); onChanged(); };
  const c = query.data;
  return (
    <Card data-testid="tt-timeline"><CardBody>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-semibold">{c?.name ?? "Loading…"}</h2>
          {c ? <p className="text-sm text-zinc-500">{c.what}{c.url ? <> · <a className="underline" href={c.url} target="_blank" rel="noreferrer">{c.url}</a></> : null}</p> : null}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" data-testid="tt-company-edit" onClick={() => setEditingCompany(!editingCompany)}>Edit</Button>
          <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
        </div>
      </div>
      {c && editingCompany ? <CompanyForm key={c.id} id={c.id} name={c.name} what={c.what ?? ""} url={c.url ?? ""} onDone={() => { setEditingCompany(false); changed(); }} /> : null}
      <ol className="mt-3 space-y-3 border-l border-zinc-200 pl-4">
        {c?.entries.map(e => (
          <li key={e.id} className="text-sm" data-testid={`tt-entry-${e.id}`}>
            {editing === e.id ? (
              <EntryForm id={id} entry={e} sources={sources} onDone={() => { setEditing(null); changed(); }} onCancel={() => setEditing(null)} />
            ) : (
              <>
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="text-xs text-zinc-500">{dateWords(e.happened_on)}</span>
                  <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs">{e.kind_label}</span>
                  {e.url ? <a href={e.url} target="_blank" rel="noreferrer" className="font-medium underline">{e.title}</a> : <span className="font-medium">{e.title}</span>}
                  {e.sample ? <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-500">Sample</span> : null}
                </div>
                {e.why ? <p className="text-zinc-700">{`Why it matters: ${e.why}`}</p> : null}
                <p className="text-xs text-zinc-500">{`Source: ${e.source_name ?? "not given"} · confidence: ${e.confidence}`}
                  <button type="button" className="ml-2 underline" data-testid={`tt-entry-edit-${e.id}`} onClick={() => setEditing(e.id)}>Edit</button></p>
              </>
            )}
          </li>
        ))}
      </ol>
      {c && !c.entries.length ? <p className="text-sm text-zinc-500">Nothing on the timeline yet.</p> : null}
      <div className="mt-3 border-t border-zinc-100 pt-3">
        <h3 className="text-sm font-medium">Add to the timeline</h3>
        <EntryForm id={id} sources={sources} onDone={changed} />
      </div>
    </CardBody></Card>
  );
}

function CompanyForm({ id, name: n0, what: w0, url: u0, onDone }: { id: string; name: string; what: string; url: string; onDone: () => void }) {
  const [name, setName] = useState(n0);
  const [what, setWhat] = useState(w0);
  const [url, setUrl] = useState(u0);
  const save = useMutation({
    mutationFn: async () => {
      const { error } = await api.PATCH("/api/v1/admin/think-tank/competitors/{competitor_id}", {
        params: { path: { competitor_id: id } }, body: { name: name.trim(), what: orNull(what), url: orNull(url) },
      });
      if (error) throw new Error(errorMessage(error, "Couldn’t save it."));
    },
    onSuccess: onDone,
  });
  return (
    <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]" role="group" aria-label="Edit the company">
      <input aria-label="Name" value={name} onChange={e => setName(e.target.value)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm" />
      <input aria-label="What they do" placeholder="What they do" value={what} onChange={e => setWhat(e.target.value)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm" />
      <input aria-label="Website" placeholder="Website (https://…)" value={url} onChange={e => setUrl(e.target.value)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm" />
      <Button size="sm" disabled={!name.trim() || save.isPending} data-testid="tt-company-save" onClick={() => save.mutate()}>Save</Button>
      {save.isError ? <p role="alert" className="text-sm text-red-700 sm:col-span-4">{save.error.message}</p> : null}
    </div>
  );
}

const today = () => new Date().toISOString().slice(0, 10);

function EntryForm({ id, entry, sources, onDone, onCancel }: { id: string; entry?: TtEntry; sources: TtSource[]; onDone: () => void; onCancel?: () => void }) {
  const [day, setDay] = useState(entry?.happened_on ?? today());
  const [kind, setKind] = useState<Kind>(entry?.kind ?? "launch");
  const [title, setTitle] = useState(entry?.title ?? "");
  const [url, setUrl] = useState(entry?.url ?? "");
  const [source, setSource] = useState(entry?.source_id ?? "");
  const [why, setWhy] = useState(entry?.why ?? "");
  const [confidence, setConfidence] = useState<Confidence>(entry?.confidence ?? "medium");
  const save = useMutation({
    mutationFn: async () => {
      const body = { happened_on: day, kind, title: title.trim(), url: orNull(url), source_id: source || null, why: orNull(why), confidence };
      const { error } = entry
        ? await api.PATCH("/api/v1/admin/think-tank/entries/{entry_id}", { params: { path: { entry_id: entry.id } }, body })
        : await api.POST("/api/v1/admin/think-tank/competitors/{competitor_id}/entries", { params: { path: { competitor_id: id } }, body });
      if (error) throw new Error(errorMessage(error, "Couldn’t save that."));
    },
    onSuccess: () => { if (!entry) { setTitle(""); setUrl(""); setWhy(""); } onDone(); },
  });
  return (
    <div className="mt-2 space-y-2" role="group" aria-label={entry ? "Edit the entry" : "Add an entry"}>
      <div className="grid gap-2 sm:grid-cols-3">
        <input aria-label="Date" type="date" value={day} onChange={e => setDay(e.target.value)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm" />
        <select aria-label="What happened" value={kind} onChange={e => setKind(e.target.value as Kind)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm">
          {INTEL_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select aria-label="Confidence" value={confidence} onChange={e => setConfidence(e.target.value as Confidence)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm">
          {CONFIDENCE.map(c => <option key={c} value={c}>Confidence: {c}</option>)}
        </select>
        <input aria-label="What changed" placeholder="What changed" value={title} onChange={e => setTitle(e.target.value)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm sm:col-span-2" data-testid={entry ? "tt-entry-title-edit" : "tt-entry-title"} />
        <select aria-label="Source" value={source} onChange={e => setSource(e.target.value)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm">
          <option value="">Source (optional)</option>
          {sources.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <input aria-label="Link" placeholder="Link (https://…)" value={url} onChange={e => setUrl(e.target.value)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm sm:col-span-3" />
      </div>
      <textarea aria-label="Why it matters to us" placeholder="Why it matters to us" value={why} onChange={e => setWhy(e.target.value)} className="min-h-14 w-full rounded-md border border-zinc-300 p-2 text-sm" />
      <div className="flex gap-2">
        <Button size="sm" disabled={!title.trim() || !day || save.isPending} data-testid={entry ? "tt-entry-save-edit" : "tt-entry-add"} onClick={() => save.mutate()}>{entry ? "Save" : "Add"}</Button>
        {onCancel ? <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button> : null}
      </div>
      {save.isError ? <p role="alert" className="text-sm text-red-700">{save.error.message}</p> : null}
    </div>
  );
}

function Pack({ v, onChanged }: { v: TtIntelView; onChanged: () => void }) {
  return (
    <Card data-testid="tt-pack"><CardBody>
      <h2 className="font-semibold">{PACK_TITLE}</h2>
      <p className="text-sm text-zinc-500">{v.pack.line}</p>
      <ul className="mt-2 divide-y divide-zinc-100">
        {v.pack.items.map(i => <PackRow key={i.ref} i={i} opportunities={v.opportunities} onChanged={onChanged} />)}
      </ul>
      {!v.pack.items.length ? <p className="py-2 text-sm text-zinc-500">Nothing yet that enough people said.</p> : null}
      <p className="mt-2 text-xs text-zinc-500">{v.pack.floor_line}</p>
    </CardBody></Card>
  );
}

function PackRow({ i, opportunities, onChanged }: { i: TtPackItem; opportunities: TtIntelView["opportunities"]; onChanged: () => void }) {
  const [opportunity, setOpportunity] = useState(i.opportunity_id ?? "");
  const [note, setNote] = useState(i.note ?? "");
  const save = useMutation({
    mutationFn: async () => {
      const { error } = await api.PUT("/api/v1/admin/think-tank/pack/{ref}", {
        params: { path: { ref: i.ref } }, body: { opportunity_id: opportunity || null, note: orNull(note) },
      });
      if (error) throw new Error(errorMessage(error, "Couldn’t save that."));
    },
    onSuccess: onChanged,
  });
  return (
    <li className="py-3 text-sm" data-testid={`tt-pack-${i.ref}`}>
      {i.quote ? <p className="font-medium">{`“${i.quote}”`}</p> : <p className="italic text-zinc-500">{i.quote_line}</p>}
      {i.sample ? <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-500" data-testid={`tt-pack-sample-${i.ref}`}>Sample</span> : null}
      <p className="text-xs text-zinc-500">{`${i.people_line} · ${i.status_line}`}</p>
      {i.opportunity_title ? <p className="text-xs text-emerald-800">Feeds {i.opportunity_title}{i.note ? ` · ${i.note}` : ""}</p> : i.note ? <p className="text-xs text-zinc-600">{i.note}</p> : null}
      <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
        <select aria-label="Feeds which opportunity" value={opportunity} onChange={e => setOpportunity(e.target.value)} className="h-8 rounded-md border border-zinc-300 px-2 text-xs">
          {!i.opportunity_id ? <option value="">Feeds no opportunity yet</option> : null}
          {opportunities.map(o => <option key={o.id} value={o.id}>{o.title}</option>)}
        </select>
        <input aria-label="HQ's note" placeholder="HQ's note (optional)" value={note} onChange={e => setNote(e.target.value)} className="h-8 rounded-md border border-zinc-300 px-2 text-xs" />
        <Button size="sm" variant="secondary" disabled={save.isPending} data-testid={`tt-pack-save-${i.ref}`} onClick={() => save.mutate()}>Save</Button>
      </div>
      {save.isError ? <p role="alert" className="text-sm text-red-700">{save.error.message}</p> : null}
    </li>
  );
}
